type KeyboardActions = Record<string, (event: KeyboardEvent) => void>;

/**
 * This function acts as a hub for responding to keyboard presses.
 *
 *   - Key presses involving modifier keys are ignored
 *   - All events that are handled have `stopPropagation` and `preventDefault`
 *     called on them
 *   - To improve code clarity, the name of the space key is `Space`, not ` `.
 *
 * @param event     The event to handle
 * @param actions   An object mapping key name to function
 *
 * @example
 *
 *   handleKeyboard(event, {
 *     Enter: () => { console.log('Enter pressed') },
 *     Space: () => { console.log('Space pressed') },
 *   })
 */
export function handleKeyboard(event: KeyboardEvent, actions: KeyboardActions) {
  if (anyModifiers(event)) return;

  let key = event.key;
  if (key === ' ') key = 'Space';

  if (key in actions) {
    event.preventDefault();
    event.stopPropagation();

    actions[key](event);
  }
}

/**
 * A convenience function that returns true if any combination of ctrl, alt,
 * shift, or meta (command) were pressed. We typically don't want to take action
 * in this case because screenreaders utilize these keys for navigational
 * purposes.
 */
export function anyModifiers(event: KeyboardEvent) {
  return event.metaKey || event.shiftKey || event.ctrlKey || event.altKey;
}

/**
 * Sometimes, `MouseEvent` is triggered from the keyboard by pressing `space`
 * or `enter` on a focused element.
 *
 * It's easy to differentiate these from real mouse events because they always
 * have all zeroes for all coordinate values.
 */
export function isFromKeyboard(event: MouseEvent) {
  return event.screenX === 0 && event.screenY === 0;
}

/**
 * Allows any element to be clicked on using the space or enter key when
 * focused. Designed to be spread into props.
 *
 * In general, if you can use a native `<button>` or `<a>` to accomplish this,
 * you should. This is a helper for those cases when you have to attach handlers
 * directly to custom elements.
 *
 * @example
 *
 *   <Host
 *     role="button"
 *     onClick={this.handleClick}
 *     {...keyboardClickProps()}
 *   >
 *     {...}
 *   </Host>
 */
export function keyboardClickProps({
  onKeyDown = () => {},
}: { onKeyDown?: (event: KeyboardEvent) => void } = {}) {
  return {
    onKeyDown: (event: KeyboardEvent) => {
      function simulateClick() {
        event.stopPropagation();
        (event.target as HTMLElement).click();
      }

      onKeyDown(event);

      handleKeyboard(event, {
        Space: simulateClick,
        Enter: simulateClick,
      });
    },
  };
}
